/**
 * Pure, testable derivations shared by the Design studio's three "Write" regions
 * (the {@link PipelineMap} on the left and the {@link Inspector} on the right).
 *
 * The pipeline is the honest picture of HOW an issue is built: a set of distinct
 * SOURCES (derived from the curated sections' `sources`), the SECTIONS in reading
 * order (the issue's real content blocks, each enriched with the discovered section
 * it was assembled from), and the ISSUE at the bottom. Every source gets a STABLE
 * colour from a small token palette so a section's provenance reads as coloured
 * "binding dots" with no crossing wires (the everyday, legible view; drawn edges are
 * a later "Wire" mode). Selection is by node kind + id: a section node's id IS its
 * content block id, so selecting it cross-highlights the rendered block for free.
 */
import type { ContentBlockRole, SourceMetrics, SuggestedSection, Treatment } from '@nuff/types';
import {
  CHANNEL_DISPLAY_NAMES,
  sourceDisplayName,
  sourceUrl,
} from '@nuff/types';

import type { ContentBlock, ContentBlockStatus, Issue, SourceRef } from '../../api';
import { railBlocksInOrder } from './blockEdits';
import { ROLE_LABELS } from './IssueRenderer';
import { orderedSections } from './panels';

/** Which pipeline node the inspector is bound to. A section node's id is its block id. */
export type SelectedNode =
  | { readonly kind: 'source'; readonly id: string }
  | { readonly kind: 'section'; readonly id: string }
  | { readonly kind: 'issue'; readonly id: string };

/**
 * The source-identity palette: distinct design-token hues, ordered to ALTERNATE
 * warm/cool and to keep the two near-identical tans (--color-accent-3, --color-info)
 * apart (separated by the green), so adjacent sources never blur together. Assigned
 * deterministically so a given source keeps the same colour across the map, the
 * binding dots, and the inspector chips. No new colour system: all existing tokens
 * (see packages/ui/src/tokens.css). Sources beyond the palette fall back to a neutral
 * swatch and are told apart by NAME, never by a silently reused hue.
 */
export const SOURCE_COLORS = [
  'var(--color-accent-1)', // blue (cool)
  'var(--color-info)', // gold (warm)
  'var(--color-success)', // green (cool)
  'var(--color-accent-3)', // light tan (warm)
  'var(--color-accent-2)', // periwinkle (cool)
  'var(--color-warning)', // orange (warm)
  'var(--color-danger)', // magenta (cool)
] as const;

/** The neutral swatch for sources past the palette: distinguished by name, not hue. */
export const NEUTRAL_SOURCE_COLOR = 'color-mix(in srgb, var(--color-dark) 32%, var(--surface))';

/**
 * The FOUR genuinely-distinct behaviours the editor offers, each with truly different
 * output (task #72 made the ladder real): a DETERMINISTIC roundup that writes no AI, and
 * three AI rungs (a quick digest, a short take, a deeper commentary). The five stored enum
 * values fold onto these four: `data` folds to `list` (both are the deterministic roundup);
 * `list`/`digest`/`spin`/`commentary` are themselves. Ordered least-AI -> most-AI, so the
 * ladder reads top to bottom. We persist the folded canonical value only when the owner
 * actually changes the control.
 */
export const OFFERED_TREATMENTS = ['list', 'digest', 'spin', 'commentary'] as const;
export type OfferedTreatment = (typeof OFFERED_TREATMENTS)[number];

/**
 * Fold any of the five stored treatments onto the nearest OFFERED behaviour, so a section
 * always shows a valid, truthful selection. `data` folds to `list` (both are the
 * deterministic, no-AI roundup); `list`/`digest`/`spin`/`commentary` map to themselves,
 * since each now produces genuinely distinct output.
 */
export function offeredTreatment(treatment: Treatment): OfferedTreatment {
  if (treatment === 'data' || treatment === 'list') {
    return 'list';
  }
  if (treatment === 'digest') {
    return 'digest';
  }
  if (treatment === 'commentary') {
    return 'commentary';
  }
  return 'spin';
}

/**
 * Truthful, plain labels for the four offered behaviours (the raw enum is never shown).
 * `list` is the ONLY deterministic "no AI writing" rung; the other three are AI-written and
 * framed as "our AI".
 */
export const OFFERED_TREATMENT_LABEL: Record<OfferedTreatment, string> = {
  list: 'Link roundup',
  digest: 'Quick digest',
  spin: 'Our take',
  commentary: 'Full commentary',
};

/** Honest one-line qualifier per offered behaviour: exactly what turns the sources into this section. */
export const OFFERED_TREATMENT_QUALIFIER: Record<OfferedTreatment, string> = {
  list: 'Assembled from the source, no AI writing.',
  digest: 'Our AI writes brief connected notes.',
  spin: "Our AI writes a short take in your newsletter's voice.",
  commentary: 'Our AI writes a deeper analysis.',
};

/**
 * The user-facing label for a section's treatment, folded onto its nearest OFFERED option
 * so every read-only chip (map, wire) and the editable control read the SAME honest set of
 * four names. The enum itself is never rendered.
 */
export function treatmentLabel(treatment: Treatment): string {
  return OFFERED_TREATMENT_LABEL[offeredTreatment(treatment)];
}

/**
 * The render role a treatment maps to. MIRRORS the backend's `roleForTreatment`
 * (services/api discover-handler): list/data -> links, digest -> digest, spin -> story,
 * commentary -> commentary. Each of the four behaviours has its OWN role, so this fully
 * encodes the behaviour. Used to tell, honestly, whether editing a section's treatment
 * would change its block on the next per-section regenerate: when this differs from the
 * block's current role, the block is out of sync and we prompt a regenerate. Because
 * spin and commentary now map to DISTINCT roles, switching between them (or between any
 * two of the four rungs) correctly fires the cue; only a no-op change stays quiet.
 */
export function treatmentRole(treatment: Treatment): ContentBlockRole {
  if (treatment === 'data' || treatment === 'list') {
    return 'links';
  }
  if (treatment === 'digest') {
    return 'digest';
  }
  if (treatment === 'commentary') {
    return 'commentary';
  }
  return 'story';
}

/** One distinct source across the newsletter, with its stable identity + colour. */
export interface PipelineSource {
  /** Stable identity key: the human display name (what the legend maps colour to). */
  readonly key: string;
  /** A representative ref for this source (name/url/metrics are read from it). */
  readonly ref: SourceRef;
  /** The human name, e.g. "TechCrunch" or "Google News: funding". */
  readonly name: string;
  /** A short name for the legend, e.g. "TechCrunch" / "Google News" (no query tail). */
  readonly shortName: string;
  /** The assigned palette colour (a CSS custom-property reference). */
  readonly color: string;
}

/** The stable identity of a source ref: its human display name. */
export function sourceKey(ref: SourceRef): string {
  return sourceDisplayName(ref);
}

/**
 * A short source name for tight spaces (the legend): the site's own name, or the
 * provider channel WITHOUT the query tail. The full name stays on the node + inspector.
 */
export function sourceShortName(ref: SourceRef): string {
  return ref.sourceType === 'site-feed' ? ref.siteName : CHANNEL_DISPLAY_NAMES[ref.channel];
}

/**
 * The distinct sources feeding a newsletter, in first-seen reading order (so the
 * colour assignment is deterministic), each mapped to a stable palette colour.
 * Derived from the curated sections' `sources`, de-duplicated by display name.
 * Sources past the palette get the NEUTRAL swatch (told apart by name, not hue).
 */
export function derivePipelineSources(sections: readonly SuggestedSection[]): PipelineSource[] {
  const seen = new Map<string, PipelineSource>();
  for (const section of orderedSections(sections)) {
    for (const ref of section.sources) {
      const key = sourceKey(ref);
      if (!seen.has(key)) {
        seen.set(key, {
          key,
          ref,
          name: key,
          shortName: sourceShortName(ref),
          color: SOURCE_COLORS[seen.size] ?? NEUTRAL_SOURCE_COLOR,
        });
      }
    }
  }
  return [...seen.values()];
}

/** A colour-by-source-key lookup, so section nodes can colour their binding dots. */
export function sourceColorMap(sources: readonly PipelineSource[]): ReadonlyMap<string, string> {
  return new Map(sources.map((source) => [source.key, source.color]));
}

/**
 * One section node in the pipeline: a real issue content block, enriched with the
 * discovered section it was assembled from (when it has one). Intro/sign-off blocks
 * carry no section, so they read honestly as unwired nodes (no treatment, no dots).
 */
export interface PipelineSectionNode {
  /** The content block's id: doubles as the selection id and the cross-highlight id. */
  readonly blockId: string;
  readonly role: ContentBlockRole;
  /** The visible node title: the block title, else the plain role label. */
  readonly title: string;
  readonly status: ContentBlockStatus;
  /** The discovered section this block came from, when bound (null for intro/sign-off). */
  readonly section: SuggestedSection | null;
  /** The section's Treatment (the honest "how much AI" knob), when bound. */
  readonly treatment: Treatment | null;
  /** The section's plain one-line summary, when bound. */
  readonly summary: string | null;
  /**
   * The sources feeding this section (in order, de-duplicated): a short NAME and the
   * source colour, so the binding dots read as a non-colour signal too (title/aria
   * naming the sources), not colour alone.
   */
  readonly boundSources: readonly BoundSource[];
}

/** One source feeding a section: its short name + identity colour, for the dots. */
export interface BoundSource {
  /** The source identity key (its display name), so an edge can match its node. */
  readonly key: string;
  readonly name: string;
  readonly color: string;
}

/**
 * The section nodes for the map + inspector: the issue's blocks in reading order
 * (kept first, then dismissed), each bound to its discovered section by `sectionId`.
 * Empty until an issue has loaded. This is the honest running order the reader sees,
 * so it always includes the automatic intro/sign-off blocks.
 */
export function derivePipelineSections(
  issue: Issue | null,
  sections: readonly SuggestedSection[],
  colorByKey: ReadonlyMap<string, string>,
): PipelineSectionNode[] {
  if (issue === null) {
    return [];
  }
  const sectionById = new Map(sections.map((section) => [section.sectionId, section]));
  return railBlocksInOrder(issue).map((block): PipelineSectionNode => {
    const section =
      block.sectionId !== undefined ? (sectionById.get(block.sectionId) ?? null) : null;
    const boundSources: BoundSource[] = [];
    if (section !== null) {
      const seenKeys = new Set<string>();
      for (const ref of section.sources) {
        const key = sourceKey(ref);
        if (seenKeys.has(key)) continue;
        seenKeys.add(key);
        const color = colorByKey.get(key);
        if (color !== undefined) {
          boundSources.push({ key, name: sourceShortName(ref), color });
        }
      }
    }
    return {
      blockId: block.id,
      role: block.role,
      title:
        block.title !== undefined && block.title.length > 0
          ? block.title
          : ROLE_LABELS[block.role],
      status: block.status,
      section,
      treatment: section?.treatment ?? null,
      summary: section?.summary ?? null,
      boundSources,
    };
  });
}

/**
 * The neutral stroke for the section -> Issue edges in Wire mode: a calm token colour
 * (no source identity), so the "everything converges on the Issue" leg reads as
 * structural plumbing, distinct from the colour-coded source -> section edges.
 */
export const WIRE_ISSUE_EDGE_COLOR = 'color-mix(in srgb, var(--color-dark) 42%, var(--border))';

/** One drawn edge in the Wire graph, described purely (no coordinates). */
export interface WireEdge {
  /** A stable id, unique across the edge set (used as the SVG path key). */
  readonly id: string;
  /** The node this edge leaves (a source, or a section for the Issue leg). */
  readonly from: SelectedNode;
  /** The node this edge enters (a section, or the single Issue node). */
  readonly to: SelectedNode;
  /** The stroke colour: the source's identity colour, or the neutral Issue colour. */
  readonly color: string;
  /** Which leg of the pipeline this edge is (for styling weight/dash if desired). */
  readonly kind: 'source-section' | 'section-issue';
}

/**
 * The drawn edges of the Wire graph, derived purely from the same pipeline nodes the
 * Write map uses. Two legs, left -> right:
 *
 *   SOURCE -> SECTION  one edge per (section, boundSource) pair, coloured by the
 *                      source's identity colour. A section bound to two sources yields
 *                      two edges; an unbound section (intro/sign-off) yields none.
 *   SECTION -> ISSUE   exactly one neutral edge per section node, so every section
 *                      converges on the single Issue node.
 *
 * A boundSource whose key is not among the source nodes is skipped defensively (it
 * would have no node to anchor to), so the edge set never dangles.
 */
export function deriveWireEdges(
  sources: readonly PipelineSource[],
  sectionNodes: readonly PipelineSectionNode[],
): WireEdge[] {
  const sourceKeys = new Set(sources.map((source) => source.key));
  const edges: WireEdge[] = [];
  for (const node of sectionNodes) {
    for (const bound of node.boundSources) {
      if (!sourceKeys.has(bound.key)) {
        continue;
      }
      edges.push({
        id: `src:${bound.key}->sec:${node.blockId}`,
        from: { kind: 'source', id: bound.key },
        to: { kind: 'section', id: node.blockId },
        color: bound.color,
        kind: 'source-section',
      });
    }
    edges.push({
      id: `sec:${node.blockId}->issue`,
      from: { kind: 'section', id: node.blockId },
      to: { kind: 'issue', id: 'issue' },
      color: WIRE_ISSUE_EDGE_COLOR,
      kind: 'section-issue',
    });
  }
  return edges;
}

/**
 * Whether an edge touches the selected node (i.e. is INCIDENT to it). With no
 * selection every edge is "touched" (nothing is dimmed); with a selection only the
 * selected node's own edges return true, so the graph dims the rest and even a dense
 * pipeline stays legible.
 */
export function edgeTouchesNode(edge: WireEdge, selected: SelectedNode | null): boolean {
  if (selected === null) {
    return true;
  }
  return (
    (edge.from.kind === selected.kind && edge.from.id === selected.id) ||
    (edge.to.kind === selected.kind && edge.to.id === selected.id)
  );
}

/** The section nodes a given source feeds (the reverse binding), for the inspector. */
export function sectionsForSource(
  key: string,
  sectionNodes: readonly PipelineSectionNode[],
): PipelineSectionNode[] {
  return sectionNodes.filter(
    (node) => node.section?.sources.some((ref) => sourceKey(ref) === key) ?? false,
  );
}

/** Find a section node by its block id (the selection id). */
export function sectionNodeById(
  id: string,
  sectionNodes: readonly PipelineSectionNode[],
): PipelineSectionNode | null {
  return sectionNodes.find((node) => node.blockId === id) ?? null;
}

/** Find the editable content block behind a section node's id. */
export function blockById(id: string, blocks: readonly ContentBlock[]): ContentBlock | null {
  return blocks.find((block) => block.id === id) ?? null;
}

/**
 * A plain-English volume readout for a source, e.g. "12 items · about 5 a week".
 * Cadence is dropped when it rounds to zero so we never claim "0 a week".
 */
export function volumeReadout(metrics: SourceMetrics): string {
  const items = `${String(metrics.volume)} item${metrics.volume === 1 ? '' : 's'}`;
  const perWeek = Math.round(metrics.cadencePerWeek);
  return perWeek > 0 ? `${items} · about ${String(perWeek)} a week` : items;
}

/** A plain freshness readout, e.g. "newest 4h ago" / "newest 2 days ago" / null. */
export function freshnessReadout(metrics: SourceMetrics): string | null {
  if (metrics.freshnessHours === null) {
    return null;
  }
  const hours = Math.max(0, Math.round(metrics.freshnessHours));
  if (hours < 24) {
    return `newest ${String(hours)}h ago`;
  }
  const days = Math.round(hours / 24);
  return `newest ${String(days)} day${days === 1 ? '' : 's'} ago`;
}

/** The short provider label for a source: its channel search, or a publisher feed. */
export function sourceChannelLabel(ref: SourceRef): string {
  return ref.sourceType === 'query-stream'
    ? `${CHANNEL_DISPLAY_NAMES[ref.channel]} search`
    : 'Publisher feed';
}

/** The internal query/handle behind a source, shown read-only in the inspector. */
export function sourceQuery(ref: SourceRef): string {
  return ref.sourceType === 'query-stream' ? ref.queryText : (ref.feedUrl ?? ref.siteUrl);
}

/** The human label for a block role, e.g. "Lead story" territory. Reused from the renderer. */
export function roleLabel(role: ContentBlockRole): string {
  return ROLE_LABELS[role];
}

export { sourceDisplayName, sourceUrl };
